import { useCallback, useEffect, useState } from "react";
import { WifiOff, RefreshCw, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { drain, isOnline, list as listQueue, clearAll } from "@/lib/offlineQueue";

/**
 * Global "You're offline · N queued · Sync now" banner (iter 45).
 * Renders as a slim strip pinned to the bottom of the viewport. Autoplays
 * `drain()` when the browser goes back online.
 */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(isOnline());
  const [queueSize, setQueueSize] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const items = await listQueue();
      setQueueSize(items.length);
    } catch { setQueueSize(0); }
  }, []);

  const runDrain = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      // SEC-001: drain refuses to replay items stamped with a different user.
      let uid = null;
      try { uid = sessionStorage.getItem("ledger.user_id") || null; } catch { /* ignore */ }
      const res = await drain(api, uid);
      if (res.ok.length) toast.success(`${res.ok.length} change${res.ok.length === 1 ? "" : "s"} synced`);
      if (res.failed.length) toast.error(`${res.failed.length} rejected by server — check the logs`);
      if (res.dropped?.length) toast.info(`${res.dropped.length} queued item(s) from a previous session were discarded`);
      if (res.halted) toast.info("Still offline — will retry when you're back.");
    } catch (err) {
      console.warn("[offline] drain failed:", err?.message);
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [syncing, refresh]);

  useEffect(() => {
    refresh();
    let backOnlineTimer = null;
    const onOnline = () => {
      setOnline(true); setDismissed(false);
      // Chromium sometimes fires 'online' a beat before connectivity is fully
      // restored — a short settle delay makes the first drain much more likely
      // to succeed on the first try.
      if (backOnlineTimer) clearTimeout(backOnlineTimer);
      backOnlineTimer = setTimeout(() => runDrain(), 1500);
    };
    const onOffline = () => { setOnline(false); setDismissed(false); };
    const onChanged = (e) => setQueueSize(e.detail?.size ?? 0);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("powereffect:queue-changed", onChanged);
    return () => {
      if (backOnlineTimer) clearTimeout(backOnlineTimer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("powereffect:queue-changed", onChanged);
    };
  }, [refresh, runDrain]);

  // Nothing to show: we're online AND queue is empty AND user hasn't just seen a sync.
  if (online && queueSize === 0) return null;
  if (dismissed && online) return null;

  const isFullyOffline = !online;
  const bg = isFullyOffline ? "bg-[#B4283A]" : (queueSize > 0 ? "bg-[#0369a1]" : "bg-[#2F6B4A]");
  const label = isFullyOffline
    ? `You're offline · ${queueSize} queued`
    : queueSize > 0
      ? `Back online · ${queueSize} pending`
      : "Synced";
  const Icon = isFullyOffline ? WifiOff : (queueSize > 0 ? RefreshCw : CheckCircle2);

  const discard = async () => {
    if (!window.confirm(`Discard all ${queueSize} pending changes? They will NOT be saved to the server.`)) return;
    await clearAll();
    setDismissed(true);
    toast.info("Queued changes discarded");
  };

  return (
    <div className={`fixed bottom-3 left-1/2 -translate-x-1/2 z-[9998] ${bg} text-white shadow-xl rounded-full pl-4 pr-2 py-1.5 flex items-center gap-2 max-w-[92vw]`}
         data-testid="offline-indicator" role="status">
      <Icon className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
      <span className="text-xs font-medium whitespace-nowrap">{label}</span>
      {online && queueSize > 0 && (
        <button onClick={runDrain} disabled={syncing}
                data-testid="offline-sync-now"
                className="text-[11px] bg-white/20 hover:bg-white/30 transition rounded-full px-2.5 py-0.5 disabled:opacity-60">
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      )}
      {queueSize > 0 && (
        <>
          <span className="opacity-40 text-[10px] px-0.5">·</span>
          <button onClick={discard} data-testid="offline-discard"
                  className="text-[10px] opacity-80 hover:opacity-100 underline underline-offset-2">
            Discard
          </button>
        </>
      )}
      {online && queueSize === 0 && (
        <button onClick={() => setDismissed(true)} data-testid="offline-dismiss"
                className="text-white/70 hover:text-white p-0.5 rounded-full">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
